import {
  createResource,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
} from "solid-js";
import { A, useNavigate } from "@solidjs/router";
import type {
  EnergyReport,
  SiteNode,
  SwitchTarget,
} from "@energyplatform/client";
import { api } from "../services.ts";
import { locale, t } from "../i18n.ts";
import { formatNumber, formatShare, formatTime } from "../format.ts";
import { periodRange } from "../periods.ts";
import { setScope, siteTree } from "../context.ts";
import { subtreeOf } from "../tree.ts";
import { Icon } from "../icons.tsx";
import { NodeKindIcon } from "../components/Media.tsx";
import { EnergyFlow } from "../components/EnergyFlow.tsx";
import { MetersNow } from "../components/MetersNow.tsx";
import { SitesMap } from "../components/SitesMap.tsx";
import { ErrorBox } from "../components/ErrorBox.tsx";
import { operator, PageHeader, Skeleton } from "../components/ui.tsx";

/** How often the house view asks for power now. */
const REFRESH_MS = 30_000;

const kwh = (v: number | null | undefined) =>
  v === null || v === undefined
    ? "—"
    : `${formatNumber(v, locale(), v < 100 ? 1 : 0)} kWh`;

/**
 * My places (prototype "Moje miejsca", owner): every place of the person side by side — what it uses now, today and
 * this month — and the chosen one as a house with its flows (grid, PV, battery, largest loads). Places are
 * independent: they need not form one property, so nothing is added up across them.
 */
export function PlacesPage() {
  const places = () =>
    (siteTree()?.nodes ?? [])
      .filter((n) => n.parentId === null)
      .sort((a, b) => a.name.localeCompare(b.name));
  const [chosen, setChosen] = createSignal<string>();
  const current = () => places().find((p) => p.id === chosen()) ?? places()[0];

  return (
    <>
      <PageHeader
        eyebrow={t("places.eyebrow")}
        title={t("places.title")}
        sub={t("places.sub")}
      />
      <Show
        when={places().length > 0}
        fallback={
          <Show when={siteTree()} fallback={<Skeleton lines={4} />}>
            <section class="card empty">
              <p class="muted">{t("places.none")}</p>
            </section>
          </Show>
        }
      >
        <Show when={places().length > 1}>
          <div class="grid g-places">
            <For each={places()}>
              {(p) => (
                <PlaceCard
                  place={p}
                  selected={current()?.id === p.id}
                  onSelect={() => setChosen(p.id)}
                />
              )}
            </For>
          </div>
        </Show>
        <Show when={current()} keyed>
          {(p) => <PlaceDetail place={p} />}
        </Show>
        <div class="grid g-2e">
          <ControlCard />
          <ChargingCard />
        </div>
        <SitesMap />
      </Show>
    </>
  );
}

/** One place in short: power now, today and this month, from its balance. */
function PlaceCard(props: {
  place: SiteNode;
  selected: boolean;
  onSelect: () => void;
}) {
  const [now] = createResource(
    () => props.place.id,
    (id) => api.sites.now(id),
  );
  const [today] = createResource(
    () => props.place.id,
    (id) => {
      const r = periodRange("today");
      return api.sites.energy(id, r.from, r.to, r.resolution);
    },
  );
  const [month] = createResource(
    () => props.place.id,
    (id) => {
      const r = periodRange("thisMonth");
      return api.sites.energy(id, r.from, r.to, r.resolution);
    },
  );
  return (
    <section
      class={`card place-card click${props.selected ? " sel" : ""}`}
      onClick={() => props.onSelect()}
    >
      <div class="card-h">
        <span class="place-icon">
          <NodeKindIcon kind={props.place.kind} />
        </span>
        <h2>{props.place.name}</h2>
      </div>
      <div class="card-b">
        <dl class="kv" style={{ margin: 0 }}>
          <dt>{t("places.now")}</dt>
          <dd class="num">
            <Show when={now()} fallback="—">
              {(n) =>
                n().consumption === null
                  ? "—"
                  : `${formatNumber(n().consumption, locale(), 2)} kW`
              }
            </Show>
          </dd>
          <dt>{t("places.today")}</dt>
          <dd class="num">{kwh(today()?.totals.consumption)}</dd>
          <dt>{t("places.month")}</dt>
          <dd class="num">{kwh(month()?.totals.consumption)}</dd>
          <Show when={today()?.totals.selfSufficiency != null}>
            <dt>{t("places.selfSufficiency")}</dt>
            <dd>{formatShare(today()!.totals.selfSufficiency, locale())}</dd>
          </Show>
        </dl>
      </div>
    </section>
  );
}

/** The chosen place as a house with its flows now, refreshed every half minute, and today in short. */
function PlaceDetail(props: { place: SiteNode }) {
  const navigate = useNavigate();
  const [now, { refetch }] = createResource(
    () => props.place.id,
    (id) => api.sites.now(id),
  );
  onMount(() => {
    const timer = setInterval(() => {
      api.invalidate(`/api/v1/nodes/${encodeURIComponent(props.place.id)}/now`);
      void refetch();
    }, REFRESH_MS);
    onCleanup(() => clearInterval(timer));
  });
  const [today] = createResource(
    () => props.place.id,
    (id) => {
      const r = periodRange("today");
      return api.sites.energy(id, r.from, r.to, r.resolution);
    },
  );
  const [month] = createResource(
    () => props.place.id,
    (id) => {
      const r = periodRange("thisMonth");
      return api.sites.energy(id, r.from, r.to, r.resolution);
    },
  );
  const points = () => {
    const tree = siteTree();
    if (!tree) {
      return [];
    }
    const nodes = new Set(subtreeOf(tree.nodes, props.place.id));
    return tree.points.filter((p) => nodes.has(p.nodeId));
  };
  const open = (path: string) => {
    setScope(props.place.id);
    navigate(path);
  };
  return (
    <>
      <div class="grid g-2w">
        <section class="card">
          <div class="card-h">
            <h2>{t("places.house.title", { name: props.place.name })}</h2>
            <span class="meta">
              <Show when={now()?.at} fallback="">
                {(at) =>
                  t("places.house.at", { time: formatTime(at(), locale()) })
                }
              </Show>
            </span>
          </div>
          <div class="card-b">
            <Show when={!now.error} fallback={<ErrorBox error={now.error} />}>
              <Show when={now()} fallback={<Skeleton lines={6} />}>
                {(n) => <EnergyFlow now={n()} />}
              </Show>
            </Show>
          </div>
        </section>
        <section class="card">
          <div class="card-h">
            <h2>{t("places.todayTitle")}</h2>
            <span class="meta">
              {t("places.month")}: {kwh(month()?.totals.consumption)}
            </span>
          </div>
          <div class="card-b" style={{ display: "grid", gap: "14px" }}>
            <Show when={today()} fallback={<Skeleton lines={4} />}>
              {(e) => <Today report={e()} />}
            </Show>
            <div style={{ display: "flex", gap: "8px", "flex-wrap": "wrap" }}>
              <button
                type="button"
                class="btn"
                onClick={() => open("/overview")}
              >
                <Icon name="chart" />
                {t("places.openOverview")}
              </button>
              <button
                type="button"
                class="btn"
                onClick={() => open("/analysis?type=par")}
              >
                <Icon name="bolt" />
                {t("places.meters.all")}
              </button>
            </div>
          </div>
        </section>
      </div>
      <MetersNow points={points()} placeId={props.place.id} />
    </>
  );
}

function Today(props: { report: EnergyReport }) {
  const tot = () => props.report.totals;
  return (
    <Show
      when={props.report.hasBoundary}
      fallback={
        <p class="hint" style={{ margin: 0 }}>
          {t("places.house.noBoundary")}
        </p>
      }
    >
      <div class="today-kpis">
        <div class="kpi">
          <span class="lbl">{t("places.use")}</span>
          <span class="val">{kwh(tot().consumption)}</span>
        </div>
        <div class="kpi">
          <span class="lbl">{t("places.fromGrid")}</span>
          <span class="val">{kwh(tot().import)}</span>
        </div>
        <div class="kpi">
          <span class="lbl">{t("places.toGrid")}</span>
          <span class="val">{kwh(tot().export)}</span>
        </div>
        <Show when={tot().production !== null}>
          <div class="kpi">
            <span class="lbl">{t("places.production")}</span>
            <span class="val">{kwh(tot().production)}</span>
          </div>
          <div class="kpi">
            <span class="lbl">{t("places.selfSufficiency")}</span>
            <span class="val">
              {formatShare(tot().selfSufficiency, locale())}
            </span>
          </div>
        </Show>
      </div>
    </Show>
  );
}

/** The switches of the home with their state now, turned on and off by hand (a real command to the device). */
function ControlCard() {
  const [targets, { refetch }] = createResource(() =>
    api.automations.targets(),
  );
  const [result, setResult] = createSignal<{ key: string; ok: boolean }>();
  const [busy, setBusy] = createSignal<string>();
  const send = async (x: SwitchTarget, on: boolean) => {
    const key = `${x.deviceId}|${x.channel}`;
    setBusy(key);
    try {
      const r = await api.automations.switchNow(x.deviceId, x.channel, on);
      setResult({ key, ok: r.sent });
      setTimeout(() => {
        api.invalidate("/api/v1/automations/targets");
        void refetch();
      }, 2500);
    } catch {
      setResult({ key, ok: false });
    } finally {
      setBusy(undefined);
    }
  };
  return (
    <section class="card">
      <div class="card-h">
        <h2>{t("places.control")}</h2>
        <A class="meta" href="/automations">
          {t("places.controlMore")}
        </A>
      </div>
      <Show when={targets()} fallback={<Skeleton lines={3} />}>
        <Show
          when={targets()!.length > 0}
          fallback={<div class="card-b hint">{t("places.controlNone")}</div>}
        >
          <table class="tbl">
            <tbody>
              <For each={targets()!.slice(0, 6)}>
                {(x) => {
                  const key = `${x.deviceId}|${x.channel}`;
                  return (
                    <tr>
                      <td>
                        {x.deviceName}
                        <Show when={result()?.key === key}>
                          {" "}
                          <span class={`chip ${result()!.ok ? "ok" : "crit"}`}>
                            {result()!.ok
                              ? t("automations.switchSent")
                              : t("places.controlFailed")}
                          </span>
                        </Show>
                      </td>
                      <td>
                        <span class={`chip ${x.state === "on" ? "ok" : ""}`}>
                          {t(`automations.state.${x.state ?? "unknown"}`)}
                        </span>
                      </td>
                      <td class="r" style={{ "white-space": "nowrap" }}>
                        <Show when={operator()}>
                          <button
                            type="button"
                            class="btn sm"
                            disabled={busy() === key}
                            onClick={() => void send(x, x.state !== "on")}
                          >
                            {x.state === "on"
                              ? t("automations.action.off")
                              : t("automations.action.on")}
                          </button>
                        </Show>
                      </td>
                    </tr>
                  );
                }}
              </For>
            </tbody>
          </table>
        </Show>
      </Show>
    </section>
  );
}

/** Car charging in short: each charger's mode, power and the car's charge, with the way to change them. */
function ChargingCard() {
  const [chargers] = createResource(() => api.charging.chargers());
  return (
    <section class="card">
      <div class="card-h">
        <h2>{t("places.charging")}</h2>
        <A class="meta" href="/charging">
          {t("places.chargingMore")}
        </A>
      </div>
      <Show when={chargers()} fallback={<Skeleton lines={3} />}>
        <Show
          when={chargers()!.length > 0}
          fallback={<div class="card-b hint">{t("places.chargingNone")}</div>}
        >
          <table class="tbl">
            <tbody>
              <For each={chargers()}>
                {(c) => (
                  <tr>
                    <td>
                      {c.name}
                      <div class="hint">
                        {c.charging
                          ? t("places.chargingOn")
                          : c.connected
                            ? t("places.chargingConnected")
                            : t("places.chargingIdle")}
                      </div>
                    </td>
                    <td>
                      <span class="chip">
                        {c.mode ? t(`charging.mode.${c.mode}`) : "-"}
                      </span>
                    </td>
                    <td class="num">
                      {c.powerKw === null
                        ? "-"
                        : `${formatNumber(c.powerKw, locale(), 1)} kW`}
                    </td>
                    <td class="num">
                      {c.vehicleSoc === null
                        ? ""
                        : `${formatNumber(c.vehicleSoc, locale(), 0)} %`}
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </Show>
      </Show>
    </section>
  );
}
